export interface RegressionDataPoint {
  distance_km: number
  elev_gain_m: number
  elev_loss_m: number
  actual_time_min: number
}

export interface RegressionResults {
  coefficients: {
    beta0: number
    beta1: number
    beta2: number
    beta3?: number
  }
  rSquared: number
  dataPoints: number
  useFatigue: boolean
  warnings: string[]
}

export function validateRegressionData(data: RegressionDataPoint[]): { isValid: boolean; message?: string } {
  if (data.length < 3) {
    return { isValid: false, message: 'At least 3 data points required for regression' }
  }
  
  const totalDistance = data.reduce((sum, point) => sum + point.distance_km, 0)
  if (totalDistance < 5) {
    return { isValid: false, message: 'Total distance should be at least 5km for meaningful calibration' }
  }
  
  // Check for reasonable pace ranges (3-20 min/km)
  const invalidPaces = data.filter(point => {
    const pace = point.actual_time_min / point.distance_km
    return pace < 3 || pace > 20
  })
  
  if (invalidPaces.length > 0) {
    return { 
      isValid: false, 
      message: 'Some data points have unrealistic paces (should be 3-20 min/km)' 
    }
  }
  
  return { isValid: true }
}

export function performRegression(data: RegressionDataPoint[]): RegressionResults {
  const warnings: string[] = []
  
  // Calculate features and targets
  let cumulativeDistance = 0
  const features: number[][] = []
  const targets: number[] = []
  
  for (const point of data) {
    const gainRate = point.elev_gain_m / point.distance_km
    const lossRate = Math.min(point.elev_loss_m / point.distance_km, 80) // Cap at 80 m/km
    const pace = point.actual_time_min / point.distance_km
    
    features.push([1, gainRate, lossRate]) // Intercept, gain rate, loss rate
    targets.push(pace)
    
    cumulativeDistance += point.distance_km
  }
  
  // Determine if we should include fatigue factor
  const useFatigue = cumulativeDistance >= 30 && data.length >= 6
  
  if (useFatigue) {
    // Add cumulative distance as feature
    let cumDist = 0
    features.forEach((feature, index) => {
      cumDist += data[index].distance_km
      feature.push(cumDist)
    })
  }
  
  // Perform simple linear regression using normal equation
  const result = simpleLinearRegression(features, targets)
  let beta0 = result.coefficients[0]
  let beta1 = result.coefficients[1]
  let beta2 = result.coefficients[2]
  let beta3 = useFatigue ? result.coefficients[3] : undefined
  
  // Guardrail: β2 should be ≤ 0 (downhill can't make you faster than base pace)
  if (beta2 > 0) {
    warnings.push('Downhill coefficient was positive, set to 0')
    beta2 = 0
    
    // Re-run regression without downhill term
    const constrainedFeatures = features.map(f => 
      useFatigue ? [f[0], f[1], f[3]] : [f[0], f[1]] // Intercept, uphill, [fatigue]
    )
    
    const constrainedResult = simpleLinearRegression(constrainedFeatures, targets)
    beta0 = constrainedResult.coefficients[0]
    beta1 = constrainedResult.coefficients[1]
    beta3 = useFatigue ? constrainedResult.coefficients[2] : undefined
  }
  
  // Guardrail: Reasonable ranges
  if (beta0 < 3 || beta0 > 15) {
    warnings.push('Base pace is outside normal range (3-15 min/km)')
  }
  
  if (beta1 < 0 || beta1 > 0.1) {
    warnings.push('Uphill factor is outside expected range (0-0.1)')
  }
  
  if (useFatigue && beta3 && Math.abs(beta3) > 0.01) {
    warnings.push('Fatigue factor is unusually high, check your data')
  }
  
  // Statistical warnings
  if (result.rSquared < 0.5) {
    warnings.push('Low R-squared suggests poor model fit. Consider collecting more data.')
  }
  
  return {
    coefficients: {
      beta0,
      beta1,
      beta2,
      beta3
    },
    rSquared: result.rSquared,
    dataPoints: data.length,
    useFatigue,
    warnings
  }
}

// Simple multivariate linear regression using normal equation
function simpleLinearRegression(X: number[][], y: number[]): { coefficients: number[], rSquared: number } {
  const n = X.length
  const m = X[0].length
  
  // Create augmented matrix for normal equation: X'X * β = X'y
  const XtX = Array(m).fill(0).map(() => Array(m).fill(0))
  const Xty = Array(m).fill(0)
  
  // Calculate X'X
  for (let i = 0; i < m; i++) {
    for (let j = 0; j < m; j++) {
      for (let k = 0; k < n; k++) {
        XtX[i][j] += X[k][i] * X[k][j]
      }
    }
  }
  
  // Calculate X'y
  for (let i = 0; i < m; i++) {
    for (let k = 0; k < n; k++) {
      Xty[i] += X[k][i] * y[k]
    }
  }
  
  // Solve using Gaussian elimination
  const coefficients = solveLinearSystem(XtX, Xty)
  
  // Calculate R-squared
  const predictions = X.map(row => 
    row.reduce((sum, val, idx) => sum + val * coefficients[idx], 0)
  )
  
  const meanY = y.reduce((sum, val) => sum + val, 0) / n
  const totalSumSquares = y.reduce((sum, val) => sum + Math.pow(val - meanY, 2), 0)
  const residualSumSquares = y.reduce((sum, val, i) => sum + Math.pow(val - predictions[i], 2), 0)
  const rSquared = Math.max(0, 1 - (residualSumSquares / totalSumSquares))
  
  return { coefficients, rSquared }
}

// Solve linear system Ax = b using Gaussian elimination
function solveLinearSystem(A: number[][], b: number[]): number[] {
  const n = A.length
  const augmented = A.map((row, i) => [...row, b[i]])
  
  // Forward elimination
  for (let i = 0; i < n; i++) {
    // Find pivot
    let maxRow = i
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k
      }
    }
    
    // Swap rows
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]]
    
    // Make all rows below this one 0 in current column
    for (let k = i + 1; k < n; k++) {
      const factor = augmented[k][i] / augmented[i][i]
      for (let j = i; j < n + 1; j++) {
        augmented[k][j] -= factor * augmented[i][j]
      }
    }
  }
  
  // Back substitution
  const x = Array(n).fill(0)
  for (let i = n - 1; i >= 0; i--) {
    x[i] = augmented[i][n]
    for (let j = i + 1; j < n; j++) {
      x[i] -= augmented[i][j] * x[j]
    }
    x[i] /= augmented[i][i]
  }
  
  return x
}